/**
 * The recording widget, popped out: Document Picture-in-Picture (Chrome and
 * Edge 116+, Firefox 151+), an always-on-top window over every other window,
 * as Granola floats its own.
 *
 *   pipSupported(win)        the capability gate: the API, and not the desktop app's webview
 *   openPipWindow(win, size) ask for the window (needs the viewer's press)
 *   copyPipStyles(from, to, root)   make the window look like the page, self-contained
 *   registerAutoPip(nav, handler)   Chrome's "pop out when you switch tab while capturing"
 *
 * STYLES. A PiP document starts empty. It gets three things and nothing more:
 *   - the design tokens, as computed values: every custom property the page's
 *     root resolves now (the theme, the accent and the type), written onto the
 *     window's `:root`, and written again whenever the page's theme changes;
 *   - the encapsulated styles of the components actually drawn there, found
 *     by the `_ngcontent-*` / `_nghost-*` attributes on its elements;
 *   - the page's `@font-face` rules, so the type is the same.
 * The whole app stylesheet is not copied: nothing in the window needs it.
 */

/** The browser's Document Picture-in-Picture entry point (not yet in TypeScript's DOM types). */
export interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number; disallowReturnToOpener?: boolean; preferInitialWindowPlacement?: boolean }): Promise<Window>;
  readonly window: Window | null;
}

type PipCapableWindow = Window & { documentPictureInPicture?: DocumentPictureInPicture; __TAURI_INTERNALS__?: unknown };

/**
 * Whether "Pop out" can be offered: the API is there (Chrome and Edge 116+,
 * Firefox 151+), and this is a browser tab, not the desktop app's webview,
 * which has no always-on-top window to give, nor a touch-only device. Safari,
 * older Firefox and every phone browser have no API, so the button is not drawn there.
 */
export function pipSupported(win: Window | null | undefined): boolean {
  const w = win as PipCapableWindow | null | undefined;
  if (!w || w.__TAURI_INTERNALS__) return false;
  if (typeof w.documentPictureInPicture?.requestWindow !== 'function') return false;
  // A touch-only device (a phone, a tablet) has no windows to float over, whatever its browser reports.
  return !w.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
}

/** Open the window, the widget's size. Throws as the API does (no press, already open elsewhere). */
export async function openPipWindow(win: Window, size: { width: number; height: number }): Promise<Window> {
  const api = (win as PipCapableWindow).documentPictureInPicture;
  if (!api) throw new Error('Document Picture-in-Picture is not available.');
  return api.requestWindow({ width: Math.round(size.width), height: Math.round(size.height), preferInitialWindowPlacement: false });
}

/** Every custom property the element resolves, as `--name: value;` declarations. */
export function customPropertyDeclarations(style: CSSStyleDeclaration, names: Iterable<string> = []): string {
  const seen = new Set<string>(names);
  for (let i = 0; i < style.length; i++) {
    const name = style.item(i);
    if (name.startsWith('--')) seen.add(name);
  }
  const out: string[] = [];
  for (const name of seen) {
    const value = style.getPropertyValue(name).trim();
    if (value) out.push(`${name}: ${value};`);
  }
  return out.join('\n');
}

/** The custom properties the page's own stylesheets declare: a browser that does not list them in computed style still resolves them. */
function declaredCustomProperties(doc: Document): Set<string> {
  const names = new Set<string>();
  const walk = (rules: CSSRuleList): void => {
    for (const rule of Array.from(rules)) {
      const style = (rule as CSSStyleRule).style;
      if (style) for (let i = 0; i < style.length; i++) if (style.item(i).startsWith('--')) names.add(style.item(i));
      const nested = (rule as CSSGroupingRule).cssRules;
      if (nested) walk(nested);
    }
  };
  for (const sheet of Array.from(doc.styleSheets)) {
    try {
      walk(sheet.cssRules);
    } catch {
      // A cross-origin sheet (a web font's CSS) cannot be read; it declares no tokens.
      continue;
    }
  }
  return names;
}

/** The encapsulation ids (`_ngcontent-ng-c123`) on `root` and its descendants. */
export function encapsulationIds(root: Element): Set<string> {
  const ids = new Set<string>();
  const visit = (el: Element): void => {
    for (const attr of Array.from(el.attributes)) {
      const m = /^_ng(?:content|host)-(.+)$/.exec(attr.name);
      if (m) ids.add(m[1]);
    }
    for (const child of Array.from(el.children)) visit(child);
  };
  visit(root);
  return ids;
}

/** The page's style elements that style any of `ids`. */
export function stylesFor(doc: Document, ids: ReadonlySet<string>): HTMLStyleElement[] {
  if (!ids.size) return [];
  return Array.from(doc.querySelectorAll('style')).filter((style) => {
    const text = style.textContent ?? '';
    for (const id of ids) if (text.includes(`_ngcontent-${id}`) || text.includes(`_nghost-${id}`)) return true;
    return false;
  });
}

function fontFaces(doc: Document): string {
  const out: string[] = [];
  for (const sheet of Array.from(doc.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) if (rule.type === CSSRule.FONT_FACE_RULE) out.push(rule.cssText);
    } catch {
      // Cross-origin (a hosted font's CSS): the link itself is copied instead.
      continue;
    }
  }
  return out.join('\n');
}

/** The base the window draws on: no margin, the page's plane, the page's type. */
const PIP_BASE = `
  html, body { margin: 0; height: 100%; }
  body {
    display: flex; align-items: stretch; justify-content: stretch; overflow: hidden;
    background: var(--surface-raised, Canvas); color: var(--text, CanvasText);
    font-family: var(--font-sans, system-ui, sans-serif);
    -webkit-font-smoothing: antialiased;
  }
`;

/**
 * Dress the PiP document: tokens, the drawn components' styles, the fonts.
 * Returns a function that writes the tokens again (call it when the page's
 * theme changes) and the teardown.
 */
export function copyPipStyles(from: Document, to: Document, drawn: Element): { refreshTokens: () => void; dispose: () => void } {
  const tokens = to.createElement('style');
  tokens.setAttribute('data-pip', 'tokens');
  const declared = declaredCustomProperties(from);
  const writeTokens = (): void => {
    const view = from.defaultView;
    if (!view) return;
    const html = from.documentElement;
    tokens.textContent = `:root {\n${customPropertyDeclarations(view.getComputedStyle(html), declared)}\n}\n`;
    const scheme = view.getComputedStyle(html).colorScheme;
    if (scheme) to.documentElement.style.colorScheme = scheme;
    const theme = html.getAttribute('data-theme');
    if (theme) to.documentElement.setAttribute('data-theme', theme);
    else to.documentElement.removeAttribute('data-theme');
  };
  writeTokens();

  const base = to.createElement('style');
  base.setAttribute('data-pip', 'base');
  base.textContent = `${PIP_BASE}\n${fontFaces(from)}`;
  to.head.append(tokens, base);

  // Hosted fonts (a cross-origin stylesheet) come as their link.
  for (const link of Array.from(from.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))) {
    if (/fonts\.(googleapis|gstatic)\.com/.test(link.href)) to.head.append(link.cloneNode(true));
  }
  for (const style of stylesFor(from, encapsulationIds(drawn))) to.head.append(style.cloneNode(true));

  // The page's theme can change while the window is open: follow it.
  const observer =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver(() => writeTokens());
  observer?.observe(from.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style', 'data-font'] });
  return { refreshTokens: writeTokens, dispose: () => observer?.disconnect() };
}

/**
 * Chrome 120+: pop out by itself when the viewer switches tab while this tab
 * captures the microphone (the Media Session `enterpictureinpicture` action,
 * which needs no press). Opt-in only: the widget registers it while the viewer
 * has turned it on and this tab records. Returns the teardown, or null where
 * the action does not exist.
 */
export function registerAutoPip(nav: Navigator | null | undefined, handler: () => void): (() => void) | null {
  const session = nav?.mediaSession;
  if (!session || typeof session.setActionHandler !== 'function') return null;
  try {
    // Not in TypeScript's MediaSessionAction union yet.
    session.setActionHandler('enterpictureinpicture' as MediaSessionAction, handler);
  } catch {
    // An action this browser does not know throws a TypeError: no automatic pop-out here.
    return null;
  }
  return () => {
    try {
      session.setActionHandler('enterpictureinpicture' as MediaSessionAction, null);
    } catch (error) {
      console.warn('[FlowRecordingWidget] could not stop the automatic pop-out:', error);
    }
  };
}
